import { yupResolver } from "@hookform/resolvers";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useHistory } from "react-router";
import * as yup from "yup";
import Select from "../wombat-ui/components/Form/Select";
import useAddNotification from "../../hooks/useAddNotification";
import useInsertCampaign from "../../graphql/hooks/mutations/useInsertCampaign";
import useTranslations from "../../hooks/useTranslations";
import useViewer from "../../hooks/useViewer";
import { PlusIcon, EditIcon } from "../icons";
import {
  Button,
  Container,
  ScreenHeader,
  ScreenContainer,
  Input,
  Textarea,
  Link,
  RadioGroup,
  Radio,
} from "../wombat-ui";
import { GET_VIEWER } from "../../graphql/hooks/queries/useGetViewer";

type CampaignInput = {
  id: string;
  name: string;
  description: string;
  reminderGap: number;
  reminderEnabled: string;
  companyId: string;
};

type CampaignFormProps = {
  defaultValues?: CampaignInput;
};

const CampaignForm: React.FC<CampaignFormProps> = ({
  defaultValues = {
    userIds: [],
  },
}) => {
  const t = useTranslations();
  const history = useHistory();
  const {
    company: { id: companyId, users, reminderEnabled, reminderGap },
  } = useViewer();

  const [addNotification] = useAddNotification();

  const CampaignFormSchema = useMemo(
    () =>
      yup
        .object()
        .shape({
          name: yup.string().required(t("forms.errors.required") as string),
          description: yup.string(),
        })
        .required(),
    [t]
  );

  const [insertCampaign] = useInsertCampaign({
    refetchQueries: [{ query: GET_VIEWER }],
    awaitRefetchQueries: true,
  });

  const {
    register,
    unregister,
    getValues,
    setValue,
    handleSubmit,
    errors,
    setError,
    control,
  } = useForm<CampaignInput>({
    resolver: yupResolver(CampaignFormSchema),
    defaultValues: {
      name: "",
      description: "",
      reminderGap: defaultValues?.reminderGap ? defaultValues?.reminderGap : reminderGap,
      reminderEnabled: defaultValues?.reminderEnabled || reminderEnabled.toString(),
      ...defaultValues,
    },
  });

  const onSubmit = useCallback(
    ({ name, description, userIds, type, reminderEnabled, reminderGap }) => {
      insertCampaign({
        variables: {
          input: {
            id: defaultValues?.id,
            name,
            description,
            reminderEnabled: reminderEnabled === "true",
            reminderGap,
            companyId,
          },
        },
      }).then(() => {
        const action = defaultValues?.id ? "edit" : "create";

        addNotification({
          variables: {
            title: t(`campaign.notification.success.${action}.title`) as string,
            body: t(`campaign.notification.success.${action}.body`) as string,
            status: "success",
          },
        });
        history.push("/campaigns");
      });
    },
    [defaultValues]
  );

  const actions: React.ReactElement[] = useMemo(
    () => [
      <Button
        key={"action1"}
        type="submit"
        variant="primary"
        form="addCampaignForm"
      >
        {t("forms.actions.send")}
      </Button>,
    ],
    []
  );

  useEffect(() => {
    register("reminderGap")
  }, [reminderEnabled]);

  const reminderSelector = useWatch({
    control: control,
    name: `reminderEnabled`,
  });

  return (
    <ScreenContainer>
      <ScreenHeader
        icon={defaultValues.id ? EditIcon : PlusIcon}
        title={defaultValues.name || (t("campaign.createCampaign") as string)}
        actions={actions}
      />
      <Container>
        <form id="addCampaignForm" onSubmit={handleSubmit(onSubmit)}>
          <div className="relative block mt-6">
            <div className="grid grid-cols-1 col-span-4 pb-8 mt-6 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <Input
                  id="campaign-name"
                  label={t("forms.fields.name") as string}
                  error={errors?.name}
                  name={`name`}
                  ref={register({ required: true })}
                />
              </div>
              <div className="sm:col-span-4">
                <Textarea
                  id="campaign-description"
                  label={t("forms.fields.description") as string}
                  rows={3}
                  error={errors?.description}
                  name={`description`}
                  ref={register}
                />
              </div>
              <div className="sm:col-span-4">
                <RadioGroup
                  title={t("company.reminderEnabledLabel")}
                  description={t("company.reminderEnabledDescription")}
                >
                  <Radio
                    id={`reminder-true`}
                    label={t("forms.actions.yes")}
                    name="reminderEnabled"
                    value={"true"}
                    ref={register}
                  />
                  <Radio
                    id={`reminder-false`}
                    label={t("forms.actions.no")}
                    name="reminderEnabled"
                    value={"false"}
                    ref={register}
                  />
                </RadioGroup>
              </div>
              {reminderSelector === "true" && (
                <div className="sm:col-span-4">
                  <Input
                    label={t("company.reminderGapLabel") as string}
                    type="number"
                    size="small"
                    min={1}
                    id="reminderGap"
                    name="reminderGap"
                    ref={register({ valueAsNumber: true })}
                    defaultValue={reminderGap || undefined}
                  />
                </div>
              )}
            </div>
          </div>
        </form>
      </Container>
    </ScreenContainer>
  );
};

export default CampaignForm;
